import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { CreditCard, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import AddCardModal from '@/components/AddCardModal';
import axios from 'axios';
import { toast } from 'sonner';

const PaymentMethodSelector = ({ isDark, onPaymentMethodSelected, selectedMethodId }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(selectedMethodId || null);

  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  useEffect(() => {
    // Auto-select default card or first card
    if (paymentMethods.length > 0 && !selectedMethod) {
      const defaultCard = paymentMethods.find(m => m.isDefault);
      const cardToSelect = defaultCard || paymentMethods[0];
      setSelectedMethod(cardToSelect.id);
      if (onPaymentMethodSelected) {
        onPaymentMethodSelected(cardToSelect.id);
      }
    }
  }, [paymentMethods]);

  const loadPaymentMethods = async () => {
    try {
      setLoadingMethods(true);
      const userEmail = user.email || 'test@shootzu.com';
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/methods?user_email=${userEmail}`
      );
      setPaymentMethods(response.data.methods || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      toast.error('Failed to load payment methods');
      setPaymentMethods([]);
    } finally {
      setLoadingMethods(false);
    }
  };

  const handleSelectMethod = (methodId) => {
    setSelectedMethod(methodId);
    if (onPaymentMethodSelected) {
      onPaymentMethodSelected(methodId);
    }
  };

  const handleCardAdded = () => {
    loadPaymentMethods();
    setShowAddCardModal(false);
  };

  if (loadingMethods) {
    return (
      <div
        className="text-center"
        style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', fontFamily: 'var(--sz-font-body)' }}
      >
        <p className="text-sm" style={{ color: 'rgba(255,255,255,.55)' }}>Loading payment methods...</p>
      </div>
    );
  }

  if (paymentMethods.length === 0) {
    return (
      <div className="space-y-3" style={{ fontFamily: 'var(--sz-font-body)' }}>
        <div
          className="text-center"
          style={{ padding: 24, borderRadius: 12, border: '1.5px dashed rgba(255,255,255,.18)' }}
        >
          <CreditCard className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(255,255,255,.4)' }} />
          <p className="text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,.8)' }}>
            No payment method added
          </p>
          <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,.55)' }}>
            Add a credit card to complete this transaction
          </p>
          <Button
            onClick={() => setShowAddCardModal(true)}
            size="sm"
            style={{ background: '#28C0D0', color: '#0C1120', border: 'none' }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Payment Method
          </Button>
        </div>

        <AddCardModal
          isOpen={showAddCardModal}
          onClose={() => setShowAddCardModal(false)}
          onSuccess={handleCardAdded}
          isDark={isDark}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3" style={{ fontFamily: 'var(--sz-font-body)' }}>
      <div className="text-sm font-medium mb-2" style={{ color: '#fff' }}>
        Select Payment Method
      </div>

      {/* Saved Cards */}
      <div className="space-y-2">
        {paymentMethods.map((method) => {
          const isSelected = selectedMethod === method.id;
          return (
          <div
            key={method.id}
            onClick={() => handleSelectMethod(method.id)}
            className="cursor-pointer transition-all"
            style={{
              padding: 13,
              borderRadius: 12,
              border: isSelected ? '1px solid #28C0D0' : '1px solid rgba(255,255,255,.12)',
              background: isSelected ? 'rgba(40,192,208,.08)' : 'rgba(255,255,255,.045)',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: isSelected ? '#28C0D0' : 'rgba(255,255,255,.08)' }}
                >
                  <CreditCard className="w-5 h-5" style={{ color: isSelected ? '#0C1120' : 'rgba(255,255,255,.6)' }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium" style={{ color: '#fff', fontFamily: 'var(--sz-font-mono)' }}>
                      {method.brand} •••• {method.last4}
                    </p>
                    {method.isDefault && (
                      <Badge style={{ background: '#28C0D0', color: '#0C1120' }} className="text-xs">Default</Badge>
                    )}
                  </div>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,.5)' }}>
                    Expires {method.exp_month}/{method.exp_year}
                  </p>
                </div>
              </div>
              {isSelected && (
                <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#28C0D0' }}>
                  <svg className="w-3 h-3" style={{ color: '#0C1120' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* Add New Card Button */}
      <Button
        onClick={() => setShowAddCardModal(true)}
        variant="outline"
        className="w-full"
        size="sm"
        style={{ background: 'transparent', border: '1.5px dashed rgba(255,255,255,.18)', color: '#28C0D0' }}
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Another Card
      </Button>

      {/* Add Card Modal */}
      <AddCardModal
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        onSuccess={handleCardAdded}
        isDark={isDark}
      />
    </div>
  );
};

export default PaymentMethodSelector;
